<template>
  <AdminLayout>
    <div class="announcement-management">
      <h2>公告管理</h2>

      <!-- 新建公告 -->
      <section class="create-section">
        <h3>新建公告</h3>
        <form @submit.prevent="createAnnouncement" class="create-form">
          <div class="form-row">
            <div class="form-group" style="flex: 1;">
              <label>公告标题</label>
              <input
                type="text"
                v-model="newAnnouncement.title"
                placeholder="请输入公告标题"
                class="form-control"
                required
              />
            </div>
          </div>
          <div class="form-group">
            <label>公告内容</label>
            <textarea
              v-model="newAnnouncement.content"
              placeholder="请输入公告内容，支持HTML格式"
              class="form-control"
              rows="4"
              required
            ></textarea>
          </div>
          <div class="form-group checkbox-group">
            <label class="checkbox-label">
              <input
                type="checkbox"
                v-model="newAnnouncement.enabled"
              />
              启用此公告
            </label>
          </div>
          <button
            type="submit"
            :disabled="!newAnnouncement.title || !newAnnouncement.content || creating"
            class="btn btn-primary"
          >
            {{ creating ? '创建中...' : '创建公告' }}
          </button>
        </form>
      </section>

      <!-- 公告列表 -->
      <section class="announcements-section">
        <h3>公告列表</h3>
        
        <div class="table-actions">
          <div class="search-bar">
            <input
              type="text"
              v-model="searchKeyword"
              placeholder="搜索公告标题..."
              @keyup.enter="searchAnnouncements"
              class="form-control"
              style="max-width: 300px;"
            />
            <button @click="searchAnnouncements" class="btn btn-primary">搜索</button>
            <button @click="resetSearch" class="btn btn-secondary">重置</button>
          </div>
        </div>

        <AdminTable
          :columns="announcementColumns"
          :data="announcements"
          :total="total"
          :current-page="currentPage"
          :page-size="pageSize"
          :row-key="item => item.id"
          max-height="500px"
        >
          <template #title="{ item }">
            <strong>{{ item.title }}</strong>
          </template>

          <template #content="{ item }">
            <div v-html="item.content"></div>
          </template>

          <template #enabled="{ item }">
            <span 
              class="status-badge" 
              :class="{ 'status-enabled': item.enabled, 'status-disabled': !item.enabled }"
            >
              {{ item.enabled ? '启用' : '禁用' }}
            </span>
          </template>

          <template #createTime="{ item }">
            {{ formatDate(item.createTime) }}
          </template>

          <template #updateTime="{ item }">
            {{ formatDate(item.updateTime) }}
          </template>

          <template #actions="{ item }">
            <div class="actions-cell">
              <button @click="editAnnouncement(item)" class="btn btn-sm btn-secondary">编辑</button>
              <button 
                @click="toggleAnnouncementStatus(item)" 
                class="btn btn-sm" 
                :class="item.enabled ? 'btn-warning' : 'btn-success'"
              >
                {{ item.enabled ? '禁用' : '启用' }}
              </button>
              <button @click="deleteAnnouncement(item.id)" class="btn btn-sm btn-danger">删除</button>
            </div>
          </template>
        </AdminTable>
      </section>

      <!-- 编辑公告模态框 -->
      <AdminModal :visible="showEditModal" @close="closeEditModal">
        <template #header>
          <h3>编辑公告</h3>
        </template>
        <form @submit.prevent="updateAnnouncementSubmit" class="edit-form">
          <div class="form-group">
            <label>公告标题</label>
            <input
              type="text"
              v-model="currentEditAnnouncement.title"
              placeholder="请输入公告标题"
              class="form-control"
              required
            />
          </div>
          <div class="form-group">
            <label>公告内容</label>
            <textarea
              v-model="currentEditAnnouncement.content"
              placeholder="请输入公告内容，支持HTML格式"
              class="form-control"
              rows="6"
              required
            ></textarea>
          </div>
          <div class="form-group checkbox-group">
            <label class="checkbox-label">
              <input
                type="checkbox"
                v-model="currentEditAnnouncement.enabled"
              />
              启用此公告
            </label>
          </div>
        </form>
        <template #footer>
          <button @click="closeEditModal" class="btn btn-secondary">取消</button>
          <button @click="updateAnnouncementSubmit" class="btn btn-primary">保存修改</button>
        </template>
      </AdminModal>
    </div>
  </AdminLayout>
</template>

<script>
import AdminLayout from '@/components/admin/AdminLayout.vue'
import AdminTable from '@/components/admin/AdminTable.vue'
import AdminModal from '@/components/admin/AdminModal.vue'
import { getAnnouncementList, createAnnouncement, updateAnnouncement, deleteAnnouncement } from '@/api/announcement'

export default {
  name: 'AnnouncementManagement',
  components: {
    AdminLayout,
    AdminTable,
    AdminModal
  },
  data() {
    return {
      announcements: [],
      loading: false,
      creating: false,
      newAnnouncement: {
        title: '',
        content: '',
        enabled: true
      },
      currentEditAnnouncement: {},
      showEditModal: false,
      searchKeyword: '',
      currentPage: 1,
      pageSize: 10,
      total: 0,
      announcementColumns: [
        { key: 'title', title: '标题', width: '20%' },
        { key: 'content', title: '内容', width: '30%' },
        { key: 'enabled', title: '状态', width: '10%' },
        { key: 'createTime', title: '创建时间', width: '15%' },
        { key: 'updateTime', title: '更新时间', width: '15%' },
        { key: 'actions', title: '操作', width: '10%' }
      ]
    }
  },
  mounted() {
    this.loadAnnouncements()
  },
  methods: {
    async loadAnnouncements() {
      this.loading = true
      try {
        const response = await getAnnouncementList({
          page: this.currentPage,
          size: this.pageSize,
          keyword: this.searchKeyword
        })
        if (response.success) {
          this.announcements = response.data.records || response.data
          this.total = response.data.total || 0
        } else {
          this.$message.error(response.msg || '获取公告列表失败')
        }
      } catch (error) {
        console.error('获取公告列表失败:', error)
        this.$message.error('获取公告列表失败')
      } finally {
        this.loading = false
      }
    },

    async createAnnouncement() {
      this.creating = true
      try {
        const response = await createAnnouncement(this.newAnnouncement)
        if (response.success) {
          this.$message.success('创建公告成功')
          this.newAnnouncement = { title: '', content: '', enabled: true }
          this.loadAnnouncements()
        } else {
          this.$message.error(response.msg || '创建公告失败')
        }
      } catch (error) {
        console.error('创建公告失败:', error)
        this.$message.error('创建公告失败: ' + error.message)
      } finally {
        this.creating = false
      }
    },

    editAnnouncement(announcement) {
      this.currentEditAnnouncement = { ...announcement }
      this.showEditModal = true
    },

    closeEditModal() {
      this.showEditModal = false
    },

    async updateAnnouncementSubmit() {
      try {
        const response = await updateAnnouncement(
          this.currentEditAnnouncement.id, 
          this.currentEditAnnouncement
        )
        if (response.success) {
          this.$message.success('更新公告成功')
          this.closeEditModal()
          this.loadAnnouncements()
        } else {
          this.$message.error(response.msg || '更新公告失败')
        }
      } catch (error) {
        console.error('更新公告失败:', error)
        this.$message.error('更新公告失败: ' + error.message)
      }
    },

    async toggleAnnouncementStatus(announcement) {
      try {
        const newStatus = !announcement.enabled
        await updateAnnouncement(announcement.id, {
          ...announcement,
          enabled: newStatus
        })
        this.$message.success(`${newStatus ? '启用' : '禁用'}公告成功`)
        this.loadAnnouncements() // 重新加载列表以更新状态
      } catch (error) {
        console.error('更新公告状态失败:', error)
        this.$message.error(`${announcement.enabled ? '禁用' : '启用'}公告失败: ${error.message}`)
      }
    },

    async deleteAnnouncement(id) {
      if (!confirm('确定要删除这条公告吗？此操作不可撤销。')) {
        return
      }

      try {
        const response = await deleteAnnouncement(id)
        if (response.success) {
          this.$message.success('删除公告成功')
          this.loadAnnouncements()
        } else {
          this.$message.error(response.msg || '删除公告失败')
        }
      } catch (error) {
        console.error('删除公告失败:', error)
        this.$message.error('删除公告失败: ' + error.message)
      }
    },

    searchAnnouncements() {
      this.currentPage = 1
      this.loadAnnouncements()
    },

    resetSearch() {
      this.searchKeyword = ''
      this.currentPage = 1
      this.loadAnnouncements()
    },

    formatDate(dateString) {
      if (!dateString) return ''
      const date = new Date(dateString)
      return date.toLocaleString('zh-CN')
    }
  }
}
</script>

<style scoped>
@import '@/styles/admin-common.css';

.announcement-management h2 {
  text-align: left;
  margin-bottom: var(--spacing-xl);
  color: var(--text-primary);
}

.create-section,
.announcements-section {
  background: var(--bg-white);
  border-radius: var(--border-radius-lg);
  padding: var(--spacing-xl);
  margin-bottom: var(--spacing-xl);
  box-shadow: var(--shadow-md);
}

.create-section h3,
.announcements-section h3 {
  margin-top: 0;
  margin-bottom: var(--spacing-lg);
  color: var(--text-primary);
  border-bottom: 2px solid var(--border-color);
  padding-bottom: var(--spacing-sm);
}

.create-form {
  display: flex;
  flex-direction: column;
  gap: var(--spacing-lg);
}

.form-row {
  display: flex;
  gap: var(--spacing-lg);
  align-items: end;
}

.form-group {
  display: flex;
  flex-direction: column;
  gap: var(--spacing-xs);
}

.form-group label {
  font-weight: 500;
  color: var(--text-secondary);
}

.form-control {
  padding: var(--spacing-md);
  border: 1px solid var(--border-color);
  border-radius: var(--border-radius-sm);
  font-size: var(--font-size-md);
  transition: border-color var(--transition-fast);
}

.form-control:focus {
  outline: none;
  border-color: var(--primary-color);
}

.checkbox-group {
  flex-direction: row;
  align-items: center;
  gap: var(--spacing-sm);
}

.checkbox-label {
  display: flex;
  align-items: center;
  gap: var(--spacing-xs);
  cursor: pointer;
  font-weight: normal;
}

.table-actions {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: var(--spacing-lg);
  flex-wrap: wrap;
  gap: var(--spacing-md);
}

.search-bar {
  display: flex;
  gap: var(--spacing-sm);
  align-items: center;
  flex-wrap: wrap;
}

.actions-cell {
  display: flex;
  gap: var(--spacing-xs);
  flex-wrap: nowrap;
}

.btn-sm {
  padding: var(--spacing-xs) var(--spacing-sm);
  font-size: var(--font-size-sm);
}

.status-badge {
  padding: var(--spacing-xs) var(--spacing-sm);
  border-radius: var(--border-radius-sm);
  font-size: var(--font-size-sm);
  font-weight: 500;
}

.status-enabled {
  background-color: var(--success-bg);
  color: var(--success-color);
}

.status-disabled {
  background-color: var(--warning-bg);
  color: var(--warning-color);
}

/* 响应式设计 */
@media (max-width: 768px) {
  .form-row {
    flex-direction: column;
    align-items: stretch;
  }

  .table-actions {
    flex-direction: column;
    align-items: stretch;
  }

  .search-bar {
    width: 100%;
  }

  .actions-cell {
    flex-direction: column;
  }
}
</style>